import React, { useState, useEffect } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'
import { getStorageMode, toggleStorageMode, onStorageModeChange } from '../../lib/storageMode'
import { BackupRestoreButton } from '../BackupRestore/BackupRestoreButton'

export function StatusBar() {
  const { openTabs, activeTabId, aiMode, isCollabActive } = useAppStore()
  const activeTab = openTabs.find((t) => t.id === activeTabId)
  const [branch, setBranch] = useState('...')
  const [changedCount, setChangedCount] = useState(0)
  const [mode, setMode] = useState(getStorageMode())
  const [branchMenu, setBranchMenu] = useState(false)
  const [branches, setBranches] = useState<string[]>([])

  useEffect(() => {
    return onStorageModeChange(() => setMode(getStorageMode()))
  }, [])

  useEffect(() => {
    const load = () => {
      authFetch(apiUrl('/api/git/status'))
        .then((r) => r.json())
        .then((d) => {
          setBranch(d.initialized ? (d.branch || 'HEAD') : 'no repo')
          setChangedCount(d.files?.length ?? 0)
        })
        .catch(() => setBranch('--'))
    }
    load()
    window.addEventListener('pulse:files-changed', load)
    return () => window.removeEventListener('pulse:files-changed', load)
  }, [])

  const loadBranches = () => {
    authFetch(apiUrl('/api/git/branch'))
      .then(r => r.json())
      .then(d => setBranches(d.branches ?? []))
      .catch(() => {})
  }

  const switchBranch = (b: string) => {
    authFetch(apiUrl('/api/git/checkout'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ branch: b }),
    }).then(() => {
      setBranch(b)
      setBranchMenu(false)
      window.dispatchEvent(new CustomEvent('pulse:files-changed'))
    }).catch(() => {})
  }

  return (
    <div className="status-bar">
      <BackupRestoreButton />
      <StatusItem>
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setBranchMenu(!branchMenu); if (!branchMenu) loadBranches() }} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, display: 'inline-flex', alignItems: 'center', padding: 0 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 3 }}>
              <line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/>
            </svg>
            {branch}
            {changedCount > 0 && <span style={{ marginLeft: 4, color: 'rgba(255,255,255,0.6)' }}>+{changedCount}</span>}
          </button>
          {branchMenu && (
            <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 4, background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', padding: '4px 0', zIndex: 200, minWidth: 140 }}>
              {branches.length === 0 && <div style={{ padding: '6px 12px', fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>No branches</div>}
              {branches.map(b => (
                <button key={b} onClick={() => switchBranch(b)} style={{ width: '100%', padding: '5px 12px', background: b === branch ? 'rgba(124,110,245,0.1)' : 'none', border: 'none', color: b === branch ? 'var(--accent)' : 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 10, textAlign: 'left', cursor: 'pointer' }}>
                  {b === branch ? '● ' : '  '}{b}
                </button>
              ))}
            </div>
          )}
        </div>
      </StatusItem>

      {/* Storage mode toggle */}
      <button
        onClick={() => {
          toggleStorageMode()
          const store = useAppStore.getState()
          for (const tab of store.openTabs) { store.closeTab(tab.id) }
          window.dispatchEvent(new CustomEvent('pulse:storage-mode-changed'))
        }}
        title={mode === 'local' ? 'LOCAL mode — files on disk' : 'CLOUD mode — files in database'}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
          alignItems: 'center', gap: 3, padding: '0 6px',
          color: mode === 'local' ? 'rgba(95,212,164,0.9)' : 'rgba(124,110,245,0.9)',
          fontFamily: 'var(--font-mono)', fontSize: 10,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {mode === 'local' 
            ? <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>
            : <><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></>
          }
        </svg>
        {mode === 'local' ? 'LOCAL' : 'CLOUD'}
      </button>

      <div style={{ flex: 1 }} />

      {activeTab && (
        <>
          <StatusItem>{activeTab.language.toUpperCase()}</StatusItem>
          <StatusItem>UTF-8</StatusItem>
          <StatusItem>Spaces: 2</StatusItem>
        </>
      )}
      <StatusItem>
        <span style={{ opacity: 0.7 }}>AI:</span> {aiMode}
      </StatusItem>
      {isCollabActive && <StatusItem>collab</StatusItem>}
      <StatusItem>PULSE 0.1.0</StatusItem>
    </div>
  )
}

function StatusItem({ children, warn }: { children: React.ReactNode; warn?: boolean }) {
  return (
    <span className="status-bar__item" style={{ ...(warn ? { opacity: 0.7 } : {}), display: 'inline-flex', alignItems: 'center' }}>
      {children}
    </span>
  )
}
